import { useState, useEffect, useCallback, useRef } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { HiXMark, HiCheck, HiClipboard } from 'react-icons/hi2';
import { FaXTwitter, FaFacebook, FaWhatsapp, FaEnvelope } from 'react-icons/fa6';
import { formatTimestamp, formatTime } from '../../utils/time';
import { config } from '../../config';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  episodeId: number;
  currentTime: number;
  baseUrl?: string;
}

export function ShareModal({
  isOpen,
  onClose,
  episodeId,
  currentTime,
  baseUrl = config.playerUrl,
}: ShareModalProps) {
  const [includeTimestamp, setIncludeTimestamp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Calculate URL
  const shareUrl = includeTimestamp && currentTime > 0
    ? `${baseUrl}/${episodeId}?t=${formatTimestamp(currentTime)}`
    : `${baseUrl}/${episodeId}`;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback(async () => {
    // Clear any existing timeout
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  // Reset copied when timestamp toggle changes
  const handleTimestampChange = useCallback((checked: boolean) => {
    setIncludeTimestamp(checked);
    setCopied(false);
  }, []);

  if (!isOpen) return null;

  const encodedUrl = encodeURIComponent(shareUrl);

  const socialLinks = [
    {
      name: 'X',
      icon: <FaXTwitter size={16} />,
      href: `https://twitter.com/intent/tweet?text=${encodedUrl}`,
    },
    {
      name: 'Facebook',
      icon: <FaFacebook size={16} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: 'WhatsApp',
      icon: <FaWhatsapp size={16} />,
      href: `https://wa.me/?text=${encodedUrl}`,
    },
    {
      name: 'Email',
      icon: <FaEnvelope size={16} />,
      href: `mailto:?subject=Check out this episode&body=${encodedUrl}`,
    },
  ];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center
                  transition-colors duration-200
                  ${isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
    >
      <FocusTrap focusTrapOptions={{ initialFocus: false, allowOutsideClick: true }}>
      <div
        className={`bg-[var(--color-surface)] rounded-t-[var(--radius-2xl)] sm:rounded-[var(--radius-2xl)]
                   shadow-[var(--shadow-xl)] w-full sm:max-w-xs overflow-hidden
                   border border-[var(--color-border)]
                   transition-all duration-300 ease-out
                   ${isVisible
                     ? 'translate-y-0 opacity-100 scale-100'
                     : 'translate-y-8 opacity-0 scale-95'
                   }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h2 id="share-modal-title" className="text-sm font-semibold text-[var(--color-text)]">
            Share Episode
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 text-[var(--color-text-muted)]
                       hover:text-[var(--color-text)]
                       hover:bg-[var(--color-surface-elevated)]
                       transition-colors rounded-full
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-[var(--color-primary)]"
          >
            <HiXMark size={18} />
          </button>
        </div>

        {/* Social Links */}
        <div className="px-4 pb-4">
          <div className="flex justify-center gap-3">
            {socialLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Share on ${link.name}`}
                className="flex flex-col items-center gap-1.5 p-2
                           text-[var(--color-text-secondary)]
                           hover:text-[var(--color-text)]
                           hover:bg-[var(--color-surface-elevated)]
                           rounded-[var(--radius-lg)] transition-colors
                           focus:outline-none focus-visible:ring-2
                           focus-visible:ring-[var(--color-primary)]"
              >
                {link.icon}
                <span className="text-[10px] font-medium">{link.name}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Timestamp Toggle */}
        <div className="px-4 pb-3">
          <label className="flex items-center cursor-pointer text-xs group">
            <div className="relative">
              <input
                type="checkbox"
                checked={includeTimestamp}
                onChange={(e) => handleTimestampChange(e.target.checked)}
                className="peer sr-only"
              />
              <div className="w-8 h-5 bg-[var(--color-border)]
                              peer-checked:bg-[var(--color-primary)]
                              rounded-full transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full
                              shadow-sm transition-transform duration-200
                              peer-checked:translate-x-3" />
            </div>
            <span className="ml-2.5 text-[var(--color-text-secondary)]
                           group-hover:text-[var(--color-text)] transition-colors">
              Start at {formatTime(currentTime)}
            </span>
          </label>
        </div>

        {/* Copy Link */}
        <div className="p-3 border-t border-[var(--color-border)]
                       bg-[var(--color-surface-elevated)]">
          <div className="flex gap-2">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 px-3 py-2 text-xs
                         text-[var(--color-text)]
                         bg-[var(--color-surface)]
                         border border-[var(--color-border)]
                         rounded-[var(--radius-lg)]
                         focus:outline-none truncate"
            />
            <button
              onClick={copyToClipboard}
              aria-label={copied ? 'Copied!' : 'Copy link'}
              className={`px-3 py-2 text-xs font-medium rounded-[var(--radius-lg)]
                         transition-all duration-150
                         focus:outline-none focus-visible:ring-2
                         focus-visible:ring-[var(--color-primary)]
                         flex items-center gap-1.5
                         ${copied
                           ? 'bg-green-500 text-white'
                           : 'bg-[var(--color-primary)] text-white hover:opacity-90'
                         }`}
            >
              {copied ? (
                <>
                  <HiCheck size={14} />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <HiClipboard size={14} />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      </FocusTrap>
    </div>
  );
}
